/**
 * RiposteSkillEffect — Blades tree slash path: counter-thrust on dash-through.
 *
 * Conditional active skill: a ~300ms riposte window opens when the player
 * dashes through an enemy's active attack arc. If the player presses the
 * skill button during that window, a counter-thrust fires — dealing double
 * Flurry's base damage and applying an attack speed debuff to the target.
 *
 * The window management is driven externally (DashComponent / combat
 * system calls openWindow()). The skill itself handles activation gating,
 * damage resolution, debuff application, and cooldown management.
 *
 * Supports upgrade keys via resolver:
 * - Reading the Arc: `armorPenetration` + `durationMs` (additive)
 * - Temporal Mark: `temporalMarkDurationMs` + `markedDamageBonus` (additive)
 * - Counter Instinct: cooldown refund via CooldownHookSystem condition
 *
 * Implements SkillEffect directly with own cooldown management
 * (InstantSkillEffect has no cooldown). Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see blades-spec.md § Riposte
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface RiposteDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly combat: {
    startAttack(attackType: string): number; // returns enemies hit
  };
  readonly statusEffects: {
    apply(effectId: string, config: {
      targetEntityId: string;
      durationMs: number;
      sourceSkillId: string;
    }): void;
  };
  readonly cooldownHooks: {
    notify(event: { type: string; conditionId: string }): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface RiposteConfig {
  readonly cooldownMs: number;
  readonly damageMultiplier: number;
  readonly windowDurationMs: number;
  readonly debuffDurationMs: number;
}

const DEFAULT_CONFIG: RiposteConfig = {
  cooldownMs: 2000,                   // long cooldown tier
  damageMultiplier: 2.0,              // 2× Flurry base damage
  windowDurationMs: 300,              // 300ms counter window
  debuffDurationMs: 1500,             // attack speed debuff
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class RiposteSkillEffect implements SkillEffect {
  readonly skillId = 'riposte';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: RiposteDeps;
  private readonly config: RiposteConfig;

  private _cooldownRemaining = 0;
  private _windowOpen = false;
  private _windowTimer = 0;
  private _targetEntityId = '';
  private _activationCount = 0;
  private _totalEnemiesHit = 0;
  private _debuffsApplied = 0;
  private _conditionsEmitted = 0;
  private _resolvedDamageMultiplier = 1.0;

  // Reading the Arc buff state
  private _armorPenBuffActive = false;
  private _armorPenBuffTimer = 0;
  private _resolvedArmorPenetration = 0;
  private _resolvedArmorPenDurationMs = 0;

  // Temporal Mark state
  private _markApplied = false;
  private _resolvedMarkDurationMs = 0;
  private _resolvedMarkedDamageBonus = 0;

  constructor(deps: RiposteDeps, config: Partial<RiposteConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get windowOpen(): boolean { return this._windowOpen; }
  get windowTimer(): number { return this._windowTimer; }
  get targetEntityId(): string { return this._targetEntityId; }
  get activationCount(): number { return this._activationCount; }
  get totalEnemiesHit(): number { return this._totalEnemiesHit; }
  get debuffsApplied(): number { return this._debuffsApplied; }
  get conditionsEmitted(): number { return this._conditionsEmitted; }
  get resolvedDamageMultiplier(): number { return this._resolvedDamageMultiplier; }
  get armorPenBuffActive(): boolean { return this._armorPenBuffActive; }
  get armorPenBuffTimer(): number { return this._armorPenBuffTimer; }
  get resolvedArmorPenetration(): number { return this._resolvedArmorPenetration; }
  get resolvedArmorPenDurationMs(): number { return this._resolvedArmorPenDurationMs; }
  get markApplied(): boolean { return this._markApplied; }
  get resolvedMarkDurationMs(): number { return this._resolvedMarkDurationMs; }
  get resolvedMarkedDamageBonus(): number { return this._resolvedMarkedDamageBonus; }

  // --- Window management (called by dash detection system) ---

  /**
   * Opens the riposte counter window. Called by the dash-through
   * detection system when the player's dash passes through an
   * enemy's active attack arc.
   *
   * @param targetEntityId - the entity whose attack was dodged
   */
  openWindow(targetEntityId: string): void {
    if (this._cooldownRemaining > 0) return; // don't open window on cooldown
    this._windowOpen = true;
    this._windowTimer = this.config.windowDurationMs;
    this._targetEntityId = targetEntityId;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && this._windowOpen;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve damage multiplier
    this._resolvedDamageMultiplier = this.config.damageMultiplier *
      this.deps.resolver.getMultiplicativeEffect('riposte', 'damage');

    // Fire the counter-thrust
    const enemiesHit = this.deps.combat.startAttack('riposte');
    this._totalEnemiesHit += enemiesHit;
    this._activationCount++;

    // Close the window
    this._windowOpen = false;
    this._windowTimer = 0;

    // Apply attack speed debuff to the target
    if (enemiesHit > 0) {
      // STUB: deferred — no enemy attack speed system reads this effect
      this.deps.statusEffects.apply('attack_speed_slow', {
        targetEntityId: this._targetEntityId,
        durationMs: this.config.debuffDurationMs,
        sourceSkillId: 'riposte',
      });
      this._debuffsApplied++;

      // Reading the Arc: armor penetration buff on successful riposte
      this._resolvedArmorPenetration =
        this.deps.resolver.getAdditiveEffect('riposte', 'armorPenetration');
      this._resolvedArmorPenDurationMs =
        this.deps.resolver.getAdditiveEffect('riposte', 'durationMs');
      if (this._resolvedArmorPenetration > 0 && this._resolvedArmorPenDurationMs > 0) {
        this._armorPenBuffActive = true;
        this._armorPenBuffTimer = this._resolvedArmorPenDurationMs; // refresh, not stack
      }

      // Temporal Mark: mark the target for bonus damage from blade hits
      this._resolvedMarkDurationMs =
        this.deps.resolver.getAdditiveEffect('riposte', 'temporalMarkDurationMs');
      this._resolvedMarkedDamageBonus =
        this.deps.resolver.getAdditiveEffect('riposte', 'markedDamageBonus');
      if (this._resolvedMarkDurationMs > 0) {
        // STUB: deferred — no blade combo system consumes this mark
        this.deps.statusEffects.apply('riposte_temporal_mark', {
          targetEntityId: this._targetEntityId,
          durationMs: this._resolvedMarkDurationMs,
          sourceSkillId: 'riposte',
        });
        this._markApplied = true;
      }

      // Emit successful_riposte condition (for Counter Instinct)
      this.deps.cooldownHooks.notify({
        type: 'onCondition',
        conditionId: 'successful_riposte',
      });
      this._conditionsEmitted++;
    }

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false; // instant skill
  }

  update(delta: number): void {
    // Tick cooldown
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick window timer
    if (this._windowOpen) {
      this._windowTimer = Math.max(0, this._windowTimer - delta);
      if (this._windowTimer <= 0) {
        this._windowOpen = false;
        this._targetEntityId = '';
      }
    }

    // Tick armor penetration buff timer (Reading the Arc)
    if (this._armorPenBuffActive) {
      this._armorPenBuffTimer = Math.max(0, this._armorPenBuffTimer - delta);
      if (this._armorPenBuffTimer <= 0) {
        this._armorPenBuffActive = false;
        this._resolvedArmorPenetration = 0;
      }
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._windowOpen = false;
    this._windowTimer = 0;
    this._armorPenBuffActive = false;
    this._armorPenBuffTimer = 0;
  }
}
